import React, { useEffect, useState } from "react";
import { ExternalLink, ShoppingCart, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CURRENCY_TO_MARKETPLACE = {
  GBP: "EBAY_GB", USD: "EBAY_US", EUR: "EBAY_DE", AUD: "EBAY_AU", CAD: "EBAY_CA",
};

const MARKETPLACE_LABELS = {
  EBAY_GB: "eBay UK", EBAY_US: "eBay US", EBAY_DE: "eBay Germany",
  EBAY_AU: "eBay Australia", EBAY_CA: "eBay Canada",
};

export function EbayLiveListings({ card, format }) {
  const { user } = useAuth();
  const [marketplace, setMarketplace] = useState(
    CURRENCY_TO_MARKETPLACE[user?.currency] || "EBAY_GB");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!card?.name) return;
    setLoading(true);
    const params = new URLSearchParams({
      name: card.name,
      set_name: card.set_name || "",
      card_number: card.card_number || "",
      marketplace_id: marketplace,
    });
    api.get(`/ebay/live-listings?${params}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ listings: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [card?.name, card?.set_name, card?.card_number, marketplace]);

  if (!card?.name || card.name === "Unidentified Card") return null;

  const listings = data?.listings || [];
  const cheapest = listings[0];

  return (
    <div data-testid="ebay-live-listings" className="border border-white/10 bg-[#111113] rounded-xl p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-[#A78BFA]" />
          <h3 className="text-sm font-semibold text-white">Currently on eBay</h3>
          {data && data.count > 0 && (
            <span className="text-xs text-zinc-500">
              · {data.count} listing{data.count === 1 ? "" : "s"} available
            </span>
          )}
        </div>
        <select
          data-testid="ebay-live-marketplace-select"
          value={marketplace}
          onChange={(e) => setMarketplace(e.target.value)}
          className="text-xs bg-black/30 border border-white/10 rounded-full px-3 py-1 text-zinc-300"
        >
          {Object.entries(MARKETPLACE_LABELS).map(([id, label]) =>
            <option key={id} value={id}>{label}</option>)}
        </select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-zinc-500 py-4">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking eBay…
        </div>
      )}

      {!loading && cheapest && (
        <div className="mb-4 p-3 rounded-lg border border-[#A78BFA]/20 bg-[#A78BFA]/[0.04]">
          <p className="text-xs text-zinc-500 mb-1">Cheapest active listing</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p data-testid="ebay-cheapest-title" className="text-sm text-white truncate">{cheapest.title}</p>
              <p className="text-xs text-zinc-500">
                {cheapest.condition ? `${cheapest.condition} · ` : ""}
                seller {cheapest.seller_username}
                {cheapest.seller_feedback_pct ? ` (${cheapest.seller_feedback_pct}%)` : ""}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p data-testid="ebay-cheapest-price" className="text-lg font-mono-plex text-emerald-300 font-semibold">
                {cheapest.currency} {cheapest.price.toFixed(2)}
              </p>
              {cheapest.item_web_url && (
                <a
                  data-testid="ebay-cheapest-link"
                  href={cheapest.item_web_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#A78BFA] hover:text-white inline-flex items-center gap-1"
                >
                  Buy on eBay <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && listings.length > 1 && (
        <div className="space-y-2 text-xs">
          <p className="text-zinc-500 mb-2">Other active listings</p>
          {listings.slice(1).map((l, i) => (
            <a
              key={i}
              data-testid={`ebay-listing-${i}`}
              href={l.item_web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-2 px-2 rounded hover:bg-white/[0.02] transition-colors"
            >
              {l.image_url && <img src={l.image_url} alt="" className="w-8 h-10 object-cover rounded shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white truncate">{l.title}</p>
                <p className="text-zinc-500 truncate">
                  {l.condition || "—"} · seller {l.seller_username}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono-plex text-emerald-300">{l.currency} {l.price.toFixed(2)}</p>
              </div>
            </a>
          ))}
        </div>
      )}

      {!loading && listings.length === 0 && (
        <p data-testid="ebay-no-listings" className="text-xs text-zinc-500 py-3">
          No active {MARKETPLACE_LABELS[marketplace]} listings match this card right now.
        </p>
      )}

      {data?.search_url && (
        <a
          data-testid="ebay-see-all-link"
          href={data.search_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white"
        >
          See all matches on {MARKETPLACE_LABELS[marketplace]} <ExternalLink className="w-3 h-3" />
        </a>
      )}

      {card.current_price != null && cheapest && (
        <p data-testid="ebay-benchmark-hint" className="mt-3 text-[11px] text-zinc-600 leading-relaxed">
          {cheapest.price < card.current_price
            ? `Cheapest listing is ${(((card.current_price - cheapest.price) / card.current_price) * 100).toFixed(0)}% below CardArk's estimated value — competition is fierce.`
            : cheapest.price > card.current_price * 1.1
            ? `Cheapest listing is above CardArk's estimate — you could undercut to sell faster.`
            : `Cheapest listing is close to CardArk's estimate — a fair market.`}
        </p>
      )}
    </div>
  );
}
